import { t } from '@/i18n';
import type { ExportDirective, ExportFilter, ExportFormat, ExportScope } from '@/shared/export/types';
import type { DashboardFilters } from '../types/dashboard.types';
import { getEffectiveRange } from './dashboardFilters.utils';

/** Nombre de archivo del PDF de movimientos (BF-640). */
export const MOVEMENTS_PDF_FILENAME = 'consulta-de-movimientos.pdf';
export const MOVEMENTS_XLSX_FILENAME = 'consulta-de-movimientos.xlsx';

export interface MovementsExportInput {
  filters: DashboardFilters;
  companyName: string;
  companyId: string;
  customerId?: string | null;
  /** Usuario (Básico) al que se acota el reporte; ausente = reporte de empresa. */
  user?: { name: string; email: string } | null;
  /** Límite y consumo del usuario, para el bloque de límites del reporte. */
  userSpending?: { limit: number; spent: number } | null;
}

/**
 * Construye el directive de export del PDF de Actividad (BF-640).
 *
 * Por definición funcional envía solo rango de fechas, usuario y tarjeta; el
 * comportamiento con Estado/Tipo queda pendiente, así que hoy no se envía.
 */
export function buildMovementsExportDirective(
  input: MovementsExportInput,
  format: ExportFormat = 'pdf'
): ExportDirective {
  const range = getEffectiveRange(input.filters);

  const scope: ExportScope = {};
  if (input.user) {
    scope.user = { name: input.user.name, email: input.user.email, isBasic: true };
    if (input.userSpending && input.userSpending.limit > 0) {
      const { limit, spent } = input.userSpending;
      scope.user.limits = { limit, used: spent, available: limit - spent };
    }
  }
  const last4 = input.filters.cardLast4?.trim();
  if (last4) scope.card = { last4 };
  if (input.user) scope.hideCompany = true;

  const filters: ExportFilter[] = [
    { label: t('credits.periodLabel'), value: `${range.from} – ${range.to}` },
  ];
  const email = input.filters.email?.trim();
  if (email) filters.push({ label: t('dashboard.table.email'), value: email });
  if (last4) filters.push({ label: t('dashboard.filters.cardLabel'), value: `•••• ${last4}` });

  return {
    report: 'movements',
    format,
    companyName: input.companyName,
    companyId: input.companyId,
    customerId: input.customerId ?? null,
    range,
    scope: Object.keys(scope).length > 0 ? scope : undefined,
    filters,
  };
}
